import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Send, 
  ArrowLeft, 
  Bot, 
  User,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  Filter,
  X,
  Check,
  Building
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Logistics': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Safety': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Operations': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'HR': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Admin': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Project Management': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'IT': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Business Development': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Engineering': 'bg-teal-500/20 text-teal-400 border-teal-500/30'
};

export default function ChatPage() {
  const { departmentId } = useParams();
  const [department, setDepartment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);  // Empty = all docs
  const [showDocFilter, setShowDocFilter] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchDepartmentData();
  }, [departmentId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchDepartmentData = async () => {
    try {
      const [deptRes, docsRes] = await Promise.all([
        axios.get(`${API}/departments/${departmentId}`),
        axios.get(`${API}/documents?department_id=${departmentId}`)
      ]);
      setDepartment(deptRes.data);
      // Only show indexed documents
      const indexedDocs = docsRes.data.filter(d => d.status === 'indexed');
      setDocuments(indexedDocs);
    } catch (error) {
      toast.error('Failed to load department');
    } finally {
      setInitialLoading(false);
    }
  };

  const toggleDoc = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAllDocs = () => {
    setSelectedDocs([]);  // Empty means all
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const question = input.trim();
    setInput('');
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const payload = {
        question,
        department_id: departmentId
      };
      
      // Only send document_ids if specific docs selected
      if (selectedDocs.length > 0) {
        payload.document_ids = selectedDocs;
      }

      const response = await axios.post(`${API}/chat`, payload);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.answer,
        citations: response.data.citations,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get response');
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      setInput(question);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  if (initialLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeDocCount = selectedDocs.length === 0 ? documents.length : selectedDocs.length;

  return (
    <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border glass">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={`/dashboard/departments/${departmentId}`}
              className="p-2 rounded-lg hover:bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-foreground">Chat with {department?.name}</h1>
                {department?.category && (
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${CATEGORY_COLORS[department.category] || 'bg-gray-500/20 text-gray-400'}`}
                  >
                    {department.category}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Searching {activeDocCount} document{activeDocCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          {/* Document Filter */}
          <div className="relative">
            <Button 
              variant="outline" 
              onClick={() => setShowDocFilter(!showDocFilter)}
              className="gap-2"
              data-testid="doc-filter-btn"
            >
              <Filter className="w-4 h-4" />
              Documents
              {showDocFilter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            
            {showDocFilter && (
              <Card className="absolute right-0 top-full mt-2 w-80 z-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-foreground">Filter Documents</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={selectAllDocs}
                      className="text-xs"
                    >
                      Select All
                    </Button>
                  </div>
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {documents.map((doc) => {
                        const isSelected = selectedDocs.length === 0 || selectedDocs.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary cursor-pointer"
                            onClick={() => toggleDoc(doc.id)}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className="pointer-events-none"
                            />
                            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm text-foreground truncate">{doc.filename}</span>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <Building className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Ask about {department?.name}
            </h2>
            <p className="text-muted-foreground max-w-md">
              Ask questions about your department documents. I'll search through {documents.length} indexed document{documents.length !== 1 ? 's' : ''} to find answers.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {['What are the main procedures?', 'Summarize the key policies', 'What safety guidelines apply?'].map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="outline"
                  size="sm"
                  onClick={() => setInput(suggestion)}
                  className="text-xs"
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
              )}
              
              <div className={`max-w-[80%] ${message.role === 'user' ? 'order-1' : ''}`}>
                <div
                  className={`rounded-lg p-4 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                </div>
                
                {/* Citations */}
                {message.citations && message.citations.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Sources:</p>
                    {message.citations.map((citation, idx) => (
                      <div
                        key={idx}
                        className="text-xs p-2 rounded bg-secondary/50 border border-border"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-3 h-3 text-muted-foreground" />
                          <span className="font-medium text-foreground">{citation.document_name}</span>
                          <span className="text-muted-foreground">
                            ({citation.relevance_score}% match)
                          </span>
                        </div>
                        <p className="text-muted-foreground line-clamp-2">{citation.text_preview}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {message.role === 'user' && (
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary-foreground" />
                </div>
              )}
            </div>
          ))
        )}
        
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-primary" />
            </div>
            <div className="bg-secondary rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-muted-foreground">Thinking...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-4 border-t border-border glass">
        <form onSubmit={handleSubmit} className="flex gap-3">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question about your documents..."
            disabled={loading || documents.length === 0}
            className="flex-1"
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            disabled={loading || !input.trim() || documents.length === 0}
            className="btn-glow"
            data-testid="send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
        {documents.length === 0 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            No indexed documents. Upload and wait for indexing to complete.
          </p>
        )}
      </div>
    </div>
  );
}
